import { FakeRepository } from 'src/api/fake_repository';
import {
  MembersQueryParams,
  InvitesQueryParams,
  Users,
  Invitations,
} from 'src/api/member_repository';
import {
  UserFieldsFragment,
  InvitationFieldsFragment,
  RoleFieldsFragment,
} from 'src/types';
import { DistributionPlatform } from 'src/types/env';
import { InvitationInput } from 'src/types/member_management';

/**
 * Base class for the implementation of the Member Repository for a fake backend.
 * Uses session storage to persist data between page reloads.
 */
export abstract class FakeMemberRepository extends FakeRepository {
  abstract readonly _platform: DistributionPlatform;
  abstract readonly _roles: RoleFieldsFragment[];
  abstract readonly _membersStorageKey: string;
  abstract readonly _invitationsStorageKey: string;
  abstract readonly _initialMembers: UserFieldsFragment[];
  abstract readonly _initialInvitations: InvitationFieldsFragment[];

  private loadUsers(): UserFieldsFragment[] {
    const usersJson = sessionStorage.getItem(this._membersStorageKey);

    if (usersJson) {
      return JSON.parse(usersJson);
    }

    return this._initialMembers;
  }

  private loadInvitations(): InvitationFieldsFragment[] {
    const invitationsJson = sessionStorage.getItem(this._invitationsStorageKey);

    if (invitationsJson) {
      return JSON.parse(invitationsJson);
    }

    return this._initialInvitations;
  }

  private persistUsers(users: UserFieldsFragment[]): void {
    sessionStorage.setItem(this._membersStorageKey, JSON.stringify(users));
  }

  private persistInvitations(invitations: InvitationFieldsFragment[]): void {
    sessionStorage.setItem(
      this._invitationsStorageKey,
      JSON.stringify(invitations),
    );
  }

  getMembers(membersQueryParams: MembersQueryParams): Promise<Users> {
    let allUsers = this.loadUsers();
    const page = membersQueryParams.page || 1;
    const pageSize = membersQueryParams.perPage || 15;
    const searchText = membersQueryParams.searchText || '';

    if (searchText) {
      const searchLower = searchText.toLowerCase();
      allUsers = allUsers.filter(
        (user) =>
          (user.email && user.email.toLowerCase().includes(searchLower)) ||
          (user.name && user.name.toLowerCase().includes(searchLower)) ||
          (user.givenName &&
            user.givenName.toLowerCase().includes(searchLower)) ||
          (user.familyName &&
            user.familyName.toLowerCase().includes(searchLower)),
      );
    }

    const totalCount = allUsers.length;
    const startIndex = (page - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, totalCount);
    const paginatedUsers = allUsers.slice(startIndex, endIndex);

    return Promise.resolve({
      items: paginatedUsers,
      page: page,
      totalCount: totalCount,
    });
  }

  getInvites(invitesQueryParams: InvitesQueryParams): Promise<Invitations> {
    let allInvitations = this.loadInvitations();
    const page = invitesQueryParams.page || 1;
    const pageSize = invitesQueryParams.perPage || 20;
    const searchText = invitesQueryParams.searchText || '';
    const organizationName = invitesQueryParams.organizationName;

    if (searchText) {
      const searchLower = searchText.toLowerCase();
      allInvitations = allInvitations.filter(
        (invitation) =>
          invitation.email &&
          invitation.email.toLowerCase().includes(searchLower),
      );
    }

    if (organizationName) {
      allInvitations = allInvitations.filter(
        (invitation) => invitation.organizationId === organizationName,
      );
    }

    const totalCount = allInvitations.length;
    const startIndex = (page - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, totalCount);
    const paginatedInvitations = allInvitations.slice(startIndex, endIndex);

    return Promise.resolve({
      items: paginatedInvitations,
      page: page,
      totalCount: totalCount,
    });
  }

  inviteUsers(
    invites: InvitationInput[],
    organizationId: string,
  ): Promise<Array<InvitationFieldsFragment | null>> {
    const invitations = this.loadInvitations();
    const now = new Date().toISOString();
    const expiresAt = new Date(
      Date.now() + 7 * 24 * 60 * 60 * 1000,
    ).toISOString();

    const newInvitations: Array<InvitationFieldsFragment | null> = invites.map(
      (invite) =>
        ({
          id: `inv_${Math.random().toString(36).substring(2, 10)}`,
          email: invite.email,
          createdAt: now,
          expiresAt: expiresAt,
          inviterName: 'Current User',
          organizationId: organizationId,
          roles: invite.roleIds
            .map((roleId) =>
              this._roles.find(
                (role: RoleFieldsFragment) => role.id === roleId,
              ),
            )
            .filter((role) => role !== undefined),
        }) as InvitationFieldsFragment,
    );
    this.persistInvitations([
      ...invitations,
      ...(newInvitations.filter(Boolean) as InvitationFieldsFragment[]),
    ]);

    return Promise.resolve(newInvitations);
  }

  assignRole(
    memberId: string,
    roleIds: string[],
    _organizationId: string,
  ): Promise<void> {
    const users = this.loadUsers();
    const userIndex = users.findIndex((user) => user.userId === memberId);

    if (userIndex === -1) {
      return Promise.reject(new Error('User not found'));
    }

    users[userIndex] = {
      ...users[userIndex],
      roles: roleIds
        .map((roleId) =>
          this._roles.find((role: RoleFieldsFragment) => role.id === roleId),
        )
        .filter((role) => role !== undefined),
    };

    this.persistUsers(users);

    return Promise.resolve();
  }

  removeUser(memberId: string, _organizationId: string): Promise<void> {
    const users = this.loadUsers();
    const updatedUsers = users.filter((user) => user.userId !== memberId);

    if (updatedUsers.length === users.length) {
      return Promise.reject(new Error('User not found'));
    }

    this.persistUsers(updatedUsers);

    return Promise.resolve();
  }

  removeInvite(inviteId: string, _organizationId: string): Promise<void> {
    const invitations = this.loadInvitations();
    const filteredInvitations = invitations.filter(
      (invitation) => invitation.id !== inviteId,
    );

    if (filteredInvitations.length === invitations.length) {
      return Promise.reject(new Error('Invitation not found'));
    }

    this.persistInvitations(filteredInvitations);

    return Promise.resolve();
  }
}
